/**
 * KamAssignDialog — the single shared Assign KAM dialog (Sep 2026).
 *
 * Extracted verbatim from `BuyerManagementTab` so the Customer Detail
 * page opens the SAME dialog (AC: no new or duplicate dialog). The
 * Buyer Management list no longer opens it — Customer Detail is the
 * sole entry point.
 *
 * Endpoints (all pre-existing, unchanged):
 *   GET /buyers/kam-candidates      — active DEMAND_PLANNER users only
 *   GET /buyers/{id}/kam-history    — append-only assignment history
 *   PUT /buyers/{id}/kam            — null kam_user_id = unassign
 * History-preserving behaviour (close prior record, insert new) lives
 * server-side; this component only calls the endpoints.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import PermButton from "@/components/PermButton";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function KamAssignDialog({ buyer, open, onOpenChange, onSaved }) {
  const [kamCandidates, setKamCandidates] = useState([]);
  const [kamSelection, setKamSelection] = useState("none");
  const [kamHistory, setKamHistory] = useState([]);
  const [kamSaving, setKamSaving] = useState(false);

  useEffect(() => {
    if (!open || !buyer) return;
    setKamSelection(buyer.current_kam?.user_id || "none");
    setKamHistory([]);
    (async () => {
      try {
        const [cand, hist] = await Promise.all([
          axios.get(`${API}/buyers/kam-candidates`),
          axios.get(`${API}/buyers/${buyer.id}/kam-history`),
        ]);
        setKamCandidates(cand.data || []);
        setKamHistory(hist.data || []);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to load KAM data");
      }
    })();
  }, [open, buyer]);

  const handleKamSave = async () => {
    setKamSaving(true);
    try {
      await axios.put(`${API}/buyers/${buyer.id}/kam`, {
        kam_user_id: kamSelection === "none" ? null : kamSelection,
      });
      toast.success(kamSelection === "none" ? "KAM unassigned" : "KAM assigned");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign KAM");
    } finally {
      setKamSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="kam-assign-dialog">
        <DialogHeader>
          <DialogTitle>Assign KAM</DialogTitle>
          <DialogDescription>
            {buyer
              ? `Buyer: ${buyer.name} — only active users holding the Demand Planner role are listed.`
              : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Key Account Manager</Label>
            <Select value={kamSelection} onValueChange={setKamSelection}>
              <SelectTrigger data-testid="kam-select-trigger">
                <SelectValue placeholder="Select a KAM" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none" data-testid="kam-option-none">
                  No KAM assigned
                </SelectItem>
                {kamCandidates.map((u) => (
                  <SelectItem key={u.id} value={u.id}
                              data-testid={`kam-option-${u.id}`}>
                    {u.name} — {u.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {kamHistory.length > 0 && (
            <div data-testid="kam-history-list">
              <Label>Assignment history</Label>
              <div className="mt-1 max-h-40 overflow-y-auto space-y-1">
                {kamHistory.map((h) => (
                  <div key={h.id}
                       className="text-xs flex justify-between border rounded px-2 py-1"
                       data-testid={`kam-history-row-${h.id}`}>
                    <span>{h.kam_name} ({h.kam_email})</span>
                    <span className="text-muted-foreground font-mono">
                      {new Date(h.assigned_at).toLocaleDateString()} →{" "}
                      {h.unassigned_at
                        ? new Date(h.unassigned_at).toLocaleDateString()
                        : "current"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <PermButton
            entity="Buyer"
            action="UPDATE"
            onClick={handleKamSave}
            className="w-full"
            disabled={kamSaving}
            data-testid="save-kam-btn"
          >
            {kamSaving ? "Saving…" : "Save assignment"}
          </PermButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}
